import { Box, TextField, Typography } from '@customink/pigment-react';
import { type SxProps } from '@customink/project-service-react-components';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAtomValue } from 'jotai';
import { useFormContext } from 'react-hook-form';

import { modalStateAtom } from '@/atoms/editSizesModalAtoms';

type ProjectItemSizeFieldProps = {
  caption?: string;
  disabled?: boolean;
  label: string;
  fieldName: string;
  autoFocus?: boolean;
};

export const ProjectItemSizeField = ({
  caption,
  disabled,
  label,
  fieldName,
  autoFocus,
}: ProjectItemSizeFieldProps) => {
  const { register, watch } = useFormContext();
  const value = watch(fieldName) || '';

  const theme = useTheme();
  const isTouchDevice = useMediaQuery('(pointer: coarse), (max-width: 450px)'); // for making small input fields touch friendly
  const errorColor = theme.palette.error.main;

  const isDisabled = disabled && (value === 0 || value === '');

  const isError = disabled && value !== 0 && value !== '';

  const modalState = useAtomValue(modalStateAtom);

  const inputSx: SxProps = {
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: '36px',
    minHeight: isTouchDevice ? '36px' : '32px',
    textAlign: 'center',
    padding: '2px',
    fontSize: '14px',
    fontWeight: 'bold',
  };

  if (isError) {
    inputSx.color = errorColor;
    inputSx.fontWeight = 'bold';
  }

  const { ref, ...registerProps } = register(fieldName, {
    onChange: (event) => {
      // Prevent non-numeric characters from being displayed
      event.target.value = event.target.value.replace(/\D/g, '');
    },
    pattern: /^[0-9]*$/,
    setValueAs: (value) => Number(value),
  });
  const inputProps: InputBaseComponentProps = {
    'data-testid': 'project-item-form-field',
    inputMode: 'numeric',
    sx: inputSx,
  };

  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <Typography fontSize="13px" lineHeight="1.25" textAlign="center">
        {label}
      </Typography>
      <TextField
        autoFocus={autoFocus && modalState?.size === label}
        disabled={isDisabled}
        inputProps={inputProps}
        inputRef={ref}
        removeHelperTextSpace
        {...registerProps}
      />
      {!!caption && (
        <Typography
          bottom="4px"
          fontSize="10px"
          lineHeight="1.25"
          marginTop="2px"
          textAlign="center"
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};
